import React from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputBase,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	bulkUpdateEquipment,
	bulkCreateEquipment,
	createEquipmentColumn,
	deleteCommonEquipmentColumn,
	deleteEquipment,
	deleteEquipmentColumn,
	listEquipment,
	listEquipmentColumns,
	listHiddenColumns,
	setEquipmentStatus,
} from '../services/equipmentService'
import RateHourlyUnitPriceCell, {
	LaborRevenueRateChange,
} from '../components/equipment/RateHourlyUnitPriceCell'
import { getPayCodes } from '../services/payrollService'
import type { PayCodeResponse } from '../types/Payroll/PayCodes'
import { useProjectFeaturesContext } from '../components/project/contexts/ProjectFeaturesContext'
import type {
	EquipmentColumnDef,
	EquipmentColumnDataType,
	EquipmentColumnDto,
	EquipmentGridRow,
	EquipmentRecordDto,
	UpdateEquipmentRequest,
} from '../types/Equipment/FleetEquipmentLog'
import {
	BILLING_TYPE_COLUMN_LABEL,
	BILLING_TYPE_OPTIONS,
	calculateLaborRevenueUnitRate,
	formatBillingTypeLabel,
	RATE_HOURLY_AMOUNT_COLUMN_KEY,
	RATE_HOURLY_AMOUNT_KEY,
	RATE_HOURLY_PAY_CODE_COLUMN_KEY,
	RATE_HOURLY_PAY_CODE_KEY,
	RATE_HOURLY_PERCENT_COLUMN_KEY,
	RATE_HOURLY_PERCENT_KEY,
	isBillingTypeColumnLabel,
	isRateHourlyFactorColumnKey,
	isRatePlusHourlyBilling,
	resolveEquipmentColumnDataType,
	toApiEquipmentColumnDataType,
} from '../types/Equipment/FleetEquipmentLog'

const DEFAULT_COLUMNS: EquipmentColumnDef[] = [
	{ id: 'name', label: 'Name', isName: true, dataType: 'Text' },
	{ id: 'itemCode', label: 'Item Code', isName: false, dataType: 'Text' },
	{ id: 'unitPrice', label: 'Unit Price', isName: false, dataType: 'Currency' },
	{ id: 'description', label: 'Description', isName: false, dataType: 'Text' },
	{ id: 'onRentDate', label: 'On Rent Date', isName: false, dataType: 'Date' },
	{ id: 'offRentDate', label: 'Off Rent Date', isName: false, dataType: 'Date' },
]

function makeEmptyRow(columnIds: string[]): Record<string, string> {
	return Object.fromEntries(columnIds.map((id) => [id, ''])) as Record<string, string>
}

function isMetaColumnId(colId: string): boolean {
	return colId.startsWith('meta_')
}

function toDateInputValue(iso: string | null | undefined): string {
	if (!iso) return ''
	return iso.length >= 10 ? iso.slice(0, 10) : iso
}

function toNullableString(value: string | undefined): string | null {
	const v = (value ?? '').trim()
	return v ? v : null
}

function toNullableNumber(value: string | undefined): number | null {
	const v = (value ?? '').trim()
	if (!v) return null
	const n = Number(v)
	return Number.isFinite(n) ? n : null
}

function makeColumnKey(label: string): string {
	const slug = label
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
	return `${slug || 'col'}_${Date.now()}`
}

type EquipmentRowStatus = { isActive: boolean; isUsed: boolean }

const DEFAULT_ROW_STATUS: EquipmentRowStatus = { isActive: true, isUsed: false }

function toRowStatuses(apiRows: EquipmentRecordDto[]): Record<string, EquipmentRowStatus> {
	return Object.fromEntries(
		apiRows.map((e) => [
			String(e.id),
			{
				isActive: e.isActive ?? true,
				isUsed: e.isUsed ?? false,
			},
		]),
	)
}

const LOCKED_ROW_MESSAGE =
	'Used on time entries: name, unit price and billing type are locked. To change the rate, deactivate this line and add a new one.'

/** Contract Charges fields that can't change once a time entry uses the charge. */
function isLockableColumn(col: EquipmentColumnDef): boolean {
	return col.isName || col.id === 'unitPrice' || col.dataType === 'BillingType'
}

type RateHourlyColumnIds = { hourly?: number; rate?: number; payCode?: number }

function findRateHourlyColumnIds(apiColumns: EquipmentColumnDto[]): RateHourlyColumnIds {
	return {
		hourly: apiColumns.find((c) => c.key === RATE_HOURLY_AMOUNT_COLUMN_KEY)?.id,
		rate: apiColumns.find((c) => c.key === RATE_HOURLY_PERCENT_COLUMN_KEY)?.id,
		payCode: apiColumns.find((c) => c.key === RATE_HOURLY_PAY_CODE_COLUMN_KEY)?.id,
	}
}

async function createRateHourlyFactorColumn(
	key: string,
	name: string,
	dataType: 'Currency' | 'Number',
	sortOrder: number,
): Promise<number | undefined> {
	try {
		return await createEquipmentColumn({ key, name, dataType, sortOrder })
	} catch {
		const apiColumns = await listEquipmentColumns()
		return apiColumns.find((c) => c.key === key)?.id
	}
}

export default function FleetEquipmentLogPage() {
	const theme = useTheme()
	const { projectTemplate } = useProjectFeaturesContext()
	const isCanadaTemplate = projectTemplate === 1
	const pageTitle = isCanadaTemplate ? 'Contract Charges' : 'Fleet and Equipment Log'
	const [columns, setColumns] = useState<EquipmentColumnDef[]>(DEFAULT_COLUMNS)
	const [rows, setRows] = useState<EquipmentGridRow[]>([])
	const [rowStatuses, setRowStatuses] = useState<Record<string, EquipmentRowStatus>>({})
	const [showOnlyActive, setShowOnlyActive] = useState(false)
	const [statusPendingIds, setStatusPendingIds] = useState<ReadonlySet<string>>(new Set())
	const [initialLoading, setInitialLoading] = useState(true)
	const [payCodes, setPayCodes] = useState<PayCodeResponse[]>([])
	const [payCodesLoading, setPayCodesLoading] = useState(false)

	const getRowStatus = useCallback(
		(row: EquipmentGridRow): EquipmentRowStatus =>
			rowStatuses[row.__equipmentId] ?? DEFAULT_ROW_STATUS,
		[rowStatuses],
	)
	const isRowLocked = (row: EquipmentGridRow) => isCanadaTemplate && getRowStatus(row).isUsed
	const hasUsedRows = isCanadaTemplate && Object.values(rowStatuses).some((s) => s.isUsed)

	const displayRows = useMemo(
		() =>
			rows
				.map((row, rowIndex) => ({ row, rowIndex }))
				.filter(({ row }) => !isCanadaTemplate || !showOnlyActive || getRowStatus(row).isActive),
		[rows, isCanadaTemplate, showOnlyActive, getRowStatus],
	)

	const [addColumnOpen, setAddColumnOpen] = useState(false)
	const [newColumnLabel, setNewColumnLabel] = useState('')
	const [newColumnType, setNewColumnType] = useState<EquipmentColumnDataType>('Text')

	const visibleColumns = useMemo(
		() =>
			columns.filter(
				(col) =>
					isCanadaTemplate ||
					(col.dataType !== 'BillingType' && !isBillingTypeColumnLabel(col.label)),
			),
		[columns, isCanadaTemplate],
	)

	const hasBillingTypeColumn = useMemo(
		() => columns.some((col) => col.dataType === 'BillingType'),
		[columns],
	)

	const billingTypeColumnId = useMemo(
		() => (isCanadaTemplate ? columns.find((col) => col.dataType === 'BillingType')?.id : undefined),
		[columns, isCanadaTemplate],
	)
	const billingTypeColumnUnavailable =
		!isCanadaTemplate &&
		(newColumnType === 'BillingType' || isBillingTypeColumnLabel(newColumnLabel))

	const columnIds = useMemo(() => columns.map((c) => c.id), [columns])
	// Row-number column, data columns, and the Contract Charges status column
	const tableColumnCount = visibleColumns.length + (isCanadaTemplate ? 2 : 1)

	const rowsRef = useRef<EquipmentGridRow[]>(rows)
	const columnsRef = useRef<EquipmentColumnDef[]>(columns)
	const dirtyEquipmentIdsRef = useRef<Set<number>>(new Set())
	const saveTimerRef = useRef<number | null>(null)
	const rateHourlyColumnIdsRef = useRef<RateHourlyColumnIds>({})

	useEffect(() => {
		rowsRef.current = rows
	}, [rows])

	useEffect(() => {
		columnsRef.current = columns
	}, [columns])

	const buildUpdateRequest = useCallback((row: EquipmentGridRow): UpdateEquipmentRequest => {
		const equipmentId = Number(row.__equipmentId)

		const values: Record<number, string | null> = {}
		for (const col of columnsRef.current) {
			if (!col.metaColumnId) continue
			values[col.metaColumnId] = toNullableString(row[col.id])
		}

		const { hourly, rate, payCode } = rateHourlyColumnIdsRef.current
		if (hourly) values[hourly] = toNullableString(row[RATE_HOURLY_AMOUNT_KEY])
		if (rate) values[rate] = toNullableString(row[RATE_HOURLY_PERCENT_KEY])
		if (payCode) values[payCode] = toNullableString(row[RATE_HOURLY_PAY_CODE_KEY])

		return {
			equipmentId,
			name: (row.name ?? '').trim() || 'Untitled',
			itemCode: toNullableString(row.itemCode),
			unitPrice: toNullableNumber(row.unitPrice),
			description: toNullableString(row.description),
			onRentDate: toNullableString(row.onRentDate),
			offRentDate: toNullableString(row.offRentDate),
			values,
		}
	}, [])

	const ensureRateHourlyFactorColumns = useCallback(async () => {
		if (!isCanadaTemplate) return
		const current = rateHourlyColumnIdsRef.current
		if (current.hourly && current.rate && current.payCode) return

		const apiColumns = await listEquipmentColumns()
		const found = findRateHourlyColumnIds(apiColumns)
		const sortOrder = columnsRef.current.length

		const hourlyId =
			found.hourly ??
			(await createRateHourlyFactorColumn(
				RATE_HOURLY_AMOUNT_COLUMN_KEY,
				'Hourly Amount',
				'Currency',
				sortOrder,
			))
		const rateId =
			found.rate ??
			(await createRateHourlyFactorColumn(
				RATE_HOURLY_PERCENT_COLUMN_KEY,
				'Rate %',
				'Number',
				sortOrder + 1,
			))
		const payCodeId =
			found.payCode ??
			(await createRateHourlyFactorColumn(
				RATE_HOURLY_PAY_CODE_COLUMN_KEY,
				'Classification',
				'Number',
				sortOrder + 2,
			))

		rateHourlyColumnIdsRef.current = { hourly: hourlyId, rate: rateId, payCode: payCodeId }
	}, [isCanadaTemplate])

	const flushSavesRef = useRef<() => Promise<void>>(async () => undefined)

	const scheduleSave = useCallback(() => {
		if (saveTimerRef.current) {
			window.clearTimeout(saveTimerRef.current)
		}
		saveTimerRef.current = window.setTimeout(() => {
			void flushSavesRef.current()
		}, 800)
	}, [])

	/**
	 * The server rejects the whole batch when one of its rows became used on a time entry after this
	 * page loaded. Pick up the new lock state, put the server's locked values back on those rows, and
	 * resave so the other edits in the batch still land.
	 */
	const recoverRejectedSave = useCallback(
		async (ids: number[]) => {
			ids.forEach((id) => dirtyEquipmentIdsRef.current.add(id))
			if (!isCanadaTemplate) return

			let apiRows: EquipmentRecordDto[]
			try {
				apiRows = await listEquipment()
			} catch {
				return
			}
			setRowStatuses(toRowStatuses(apiRows))

			const billingTypeCol = columnsRef.current.find((col) => col.dataType === 'BillingType')
			const lockedValuesById = new Map<string, Record<string, string>>()
			for (const e of apiRows) {
				if (!e.isUsed || !ids.includes(e.id)) continue
				const row = rowsRef.current.find((r) => r.__equipmentId === String(e.id))
				if (!row) continue
				const lockedValues: Record<string, string> = {
					name: e.name,
					unitPrice: e.unitPrice != null ? String(e.unitPrice) : '',
				}
				if (billingTypeCol?.metaColumnId) {
					lockedValues[billingTypeCol.id] = e.values[billingTypeCol.metaColumnId] ?? ''
				}
				if (Object.entries(lockedValues).some(([key, value]) => (row[key] ?? '') !== value)) {
					lockedValuesById.set(row.__equipmentId, lockedValues)
				}
			}
			// Nothing to put back means the batch failed for another reason; it stays dirty for the next save
			if (lockedValuesById.size === 0) return

			setRows((prev) =>
				prev.map((row) => {
					const lockedValues = lockedValuesById.get(row.__equipmentId)
					return lockedValues ? { ...row, ...lockedValues } : row
				}),
			)
			scheduleSave()
		},
		[isCanadaTemplate, scheduleSave],
	)

	const flushSaves = useCallback(async () => {
		const ids = Array.from(dirtyEquipmentIdsRef.current)
		if (ids.length === 0) return

		await ensureRateHourlyFactorColumns()

		const currentRows = rowsRef.current
		const requests = ids
			.map((id) => currentRows.find((r) => Number(r.__equipmentId) === id))
			.filter(Boolean)
			.map((r) => r as EquipmentGridRow)

		const needsFactorColumns = requests.some(
			(row) =>
				(row[RATE_HOURLY_AMOUNT_KEY] ?? '').trim() !== '' ||
				(row[RATE_HOURLY_PERCENT_KEY] ?? '').trim() !== '' ||
				(row[RATE_HOURLY_PAY_CODE_KEY] ?? '').trim() !== '',
		)
		if (needsFactorColumns || rateHourlyColumnIdsRef.current.hourly || rateHourlyColumnIdsRef.current.rate) {
			await ensureRateHourlyFactorColumns()
		}

		if (requests.length === 0) return
		// Only these rows are being sent; edits typed while the request is in flight mark rows dirty again
		ids.forEach((id) => dirtyEquipmentIdsRef.current.delete(id))
		try {
			await bulkUpdateEquipment(requests.map((r) => buildUpdateRequest(r)))
		} catch {
			await recoverRejectedSave(ids)
		}
	}, [buildUpdateRequest, ensureRateHourlyFactorColumns, recoverRejectedSave])
	flushSavesRef.current = flushSaves

	useEffect(() => {
		return () => {
			if (saveTimerRef.current !== null) {
				window.clearTimeout(saveTimerRef.current)
				saveTimerRef.current = null
			}
			// Avoid losing edits if the user navigates away before the debounce fires
			flushSavesRef.current()
		}
	}, [])

	useEffect(() => {
		if (!isCanadaTemplate) return
		let cancelled = false
		setPayCodesLoading(true)
		getPayCodes()
			.then((result) => {
				if (!cancelled) setPayCodes(result)
			})
			.catch(() => {
				if (!cancelled) setPayCodes([])
			})
			.finally(() => {
				if (!cancelled) setPayCodesLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [isCanadaTemplate])

	useEffect(() => {
		let cancelled = false
		;(async () => {
			try {
				const [hiddenKeys, apiColumns, apiRows] = await Promise.all([
					listHiddenColumns(),
					listEquipmentColumns(),
					listEquipment(),
				])
				if (cancelled) return

				const hiddenSet = new Set(hiddenKeys.map((k) => k.toLowerCase()))
				const visibleCoreColumns = DEFAULT_COLUMNS.filter((c) => !hiddenSet.has(c.id.toLowerCase()))

				const visibleApiColumns = apiColumns.filter((c) => !isRateHourlyFactorColumnKey(c.key))
				rateHourlyColumnIdsRef.current = findRateHourlyColumnIds(apiColumns)

				const metaColumns: EquipmentColumnDef[] = visibleApiColumns.map((c) => ({
					id: `meta_${c.id}`,
					label: c.name,
					isName: false,
					dataType: resolveEquipmentColumnDataType(c.name, c.dataType),
					metaColumnId: c.id,
				}))

				const nextColumns = [...visibleCoreColumns, ...metaColumns]
				const nextColumnIds = nextColumns.map((c) => c.id)
				setColumns(nextColumns)

				const {
					hourly: hourlyColumnId,
					rate: rateColumnId,
					payCode: payCodeColumnId,
				} = rateHourlyColumnIdsRef.current
				const nextRows: EquipmentGridRow[] = apiRows.map((e) => {
					const equipmentId = String(e.id)
					const values = e.values ?? {}
					const base: EquipmentGridRow = {
						__equipmentId: equipmentId,
						name: e.name ?? '',
						itemCode: e.itemCode ?? '',
						unitPrice: e.unitPrice != null ? String(e.unitPrice) : '',
						[RATE_HOURLY_AMOUNT_KEY]:
							hourlyColumnId != null ? (values[hourlyColumnId] ?? '') : '',
						[RATE_HOURLY_PERCENT_KEY]:
							rateColumnId != null ? (values[rateColumnId] ?? '') : '',
						[RATE_HOURLY_PAY_CODE_KEY]:
							payCodeColumnId != null ? (values[payCodeColumnId] ?? '') : '',
						description: e.description ?? '',
						onRentDate: toDateInputValue(e.onRentDate),
						offRentDate: toDateInputValue(e.offRentDate),
					}

					for (const [colIdStr, value] of Object.entries(values)) {
						const colId = Number(colIdStr)
						if (colId === hourlyColumnId || colId === rateColumnId || colId === payCodeColumnId) {
							continue
						}
						base[`meta_${colId}`] = value ?? ''
					}

					for (const id of nextColumnIds) {
						if (!(id in base)) base[id] = ''
					}

					return base
				})

				setRows(nextRows)
				setRowStatuses(toRowStatuses(apiRows))
			} finally {
				if (!cancelled) {
					setInitialLoading(false)
				}
			}
		})()

		return () => {
			cancelled = true
		}
	}, [])

	const addRow = useCallback(async () => {
		const tempEquipmentId = `__pending_${Date.now()}_${Math.random().toString(36).slice(2)}`
		setRows((prev) => [
			...prev,
			{
				__equipmentId: tempEquipmentId,
				...makeEmptyRow(columnIds),
				name: 'Untitled',
			} as EquipmentGridRow,
		])

		try {
			const ids = await bulkCreateEquipment([
				{
					name: 'Untitled',
					itemCode: null,
					unitPrice: null,
					description: null,
					onRentDate: null,
					offRentDate: null,
					values: null,
				},
			])
			const id = ids.length > 0 ? ids[0] : null
			if (id != null) {
				setRows((prev) =>
					prev.map((r) =>
						r.__equipmentId === tempEquipmentId ? { ...r, __equipmentId: String(id) } : r,
					),
				)
			} else {
				setRows((prev) => prev.filter((r) => r.__equipmentId !== tempEquipmentId))
			}
		} catch {
			setRows((prev) => prev.filter((r) => r.__equipmentId !== tempEquipmentId))
		}
	}, [columnIds])

	const removeRow = useCallback(async (index: number) => {
		const row = rowsRef.current[index]
		if (!row) return
		await deleteEquipment(Number(row.__equipmentId))
		setRows((prev) => prev.filter((_, i) => i !== index))
	}, [])

	const toggleRowActive = useCallback(
		async (row: EquipmentGridRow) => {
			const equipmentId = Number(row.__equipmentId)
			if (!Number.isFinite(equipmentId) || equipmentId <= 0) return
			setStatusPendingIds((prev) => new Set(prev).add(row.__equipmentId))
			try {
				const isActive = !getRowStatus(row).isActive
				await setEquipmentStatus(equipmentId, isActive)
				setRowStatuses((prev) => ({
					...prev,
					[row.__equipmentId]: { ...(prev[row.__equipmentId] ?? DEFAULT_ROW_STATUS), isActive },
				}))
			} catch {
				// The API error toast already explains the failure
			} finally {
				setStatusPendingIds((prev) => {
					const next = new Set(prev)
					next.delete(row.__equipmentId)
					return next
				})
			}
		},
		[getRowStatus],
	)

	const markRowDirty = (row: EquipmentGridRow) => {
		const equipmentId = Number(row.__equipmentId)
		if (Number.isFinite(equipmentId) && equipmentId > 0) {
			dirtyEquipmentIdsRef.current.add(equipmentId)
			scheduleSave()
		}
	}

	const updateCell = useCallback(
		(rowIndex: number, colId: string, value: string) => {
			setRows((prev) =>
				prev.map((row, i) => {
					if (i !== rowIndex) return row
					const next = { ...row, [colId]: value }
					if (colId === billingTypeColumnId) {
						const previous = row[colId] ?? ''
						if (previous !== value) {
							next.unitPrice = ''
							next[RATE_HOURLY_AMOUNT_KEY] = ''
							next[RATE_HOURLY_PERCENT_KEY] = ''
							next[RATE_HOURLY_PAY_CODE_KEY] = ''
						}
					}
					markRowDirty(next)
					return next
				}),
			)
		},
		[billingTypeColumnId, scheduleSave],
	)

	const updateLaborRevenueRate = useCallback(
		(rowIndex: number, change: LaborRevenueRateChange) => {
			setRows((prev) =>
				prev.map((row, i) => {
					if (i !== rowIndex) return row
					const next: EquipmentGridRow = { ...row }
					if (change.amount !== undefined) {
						next.unitPrice = change.amount
						next[RATE_HOURLY_PAY_CODE_KEY] = ''
						next[RATE_HOURLY_PERCENT_KEY] = ''
					} else {
						if (change.payCodeId !== undefined) {
							next[RATE_HOURLY_PAY_CODE_KEY] =
								change.payCodeId != null ? String(change.payCodeId) : ''
						}
						if (change.markup !== undefined) next[RATE_HOURLY_PERCENT_KEY] = change.markup
						const payCode = payCodes.find(
							(code) => String(code.id) === next[RATE_HOURLY_PAY_CODE_KEY],
						)
						const unitRate = payCode
							? calculateLaborRevenueUnitRate(payCode.stRate, next[RATE_HOURLY_PERCENT_KEY])
							: null
						next.unitPrice = unitRate != null ? String(unitRate) : ''
					}
					markRowDirty(next)
					return next
				}),
			)
		},
		[payCodes, scheduleSave],
	)

	const handleOpenAddColumn = () => {
		setNewColumnLabel('')
		setNewColumnType('Text')
		setAddColumnOpen(true)
	}

	const handleNewColumnTypeChange = (dataType: EquipmentColumnDataType) => {
		setNewColumnType(dataType)
		if (dataType === 'BillingType') {
			setNewColumnLabel(BILLING_TYPE_COLUMN_LABEL)
		}
	}

	const handleNewColumnLabelChange = (value: string) => {
		setNewColumnLabel(value)
		if (newColumnType === 'BillingType' && !isBillingTypeColumnLabel(value)) {
			setNewColumnType('Text')
		}
	}

	const handleConfirmAddColumn = async () => {
		if (billingTypeColumnUnavailable) return
		const effectiveType: EquipmentColumnDataType =
			newColumnType === 'BillingType' && isBillingTypeColumnLabel(newColumnLabel)
				? 'BillingType'
				: newColumnType === 'BillingType'
				? 'Text'
				: newColumnType

		if (effectiveType === 'BillingType' && hasBillingTypeColumn) return

		const label =
			effectiveType === 'BillingType' ? BILLING_TYPE_COLUMN_LABEL : newColumnLabel.trim()
		if (!label) return

		const id = await createEquipmentColumn({
			key: makeColumnKey(label),
			name: label,
			dataType: toApiEquipmentColumnDataType(effectiveType),
			sortOrder: columns.length,
		})
		const colId = `meta_${id}`
		setColumns((prev) => [
			...prev,
			{ id: colId, label, isName: false, dataType: effectiveType, metaColumnId: id },
		])
		setRows((prev) => prev.map((row) => ({ ...row, [colId]: '' })))
		setAddColumnOpen(false)
		setNewColumnLabel('')
	}

	const removeColumn = useCallback(async (col: EquipmentColumnDef) => {
		if (col.isName) return

		const snapshotColumns = [...columnsRef.current]
		const snapshotRows = rowsRef.current.map((r) => ({ ...r }))

		setColumns((prev) => prev.filter((c) => c.id !== col.id))
		setRows((prev) =>
			prev.map((row) => {
				const next = { ...row }
				delete next[col.id]
				return next
			}),
		)

		try {
			if (col.metaColumnId) {
				await deleteEquipmentColumn(col.metaColumnId)
			} else if (!isMetaColumnId(col.id)) {
				await deleteCommonEquipmentColumn(col.id)
			}
		} catch {
			setColumns(snapshotColumns)
			setRows(snapshotRows)
		}
	}, [])

	const headerBg = theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50]
	const headerBorder = theme.palette.divider
	const rowHover = alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)
	const zebraEvenRowBg =
		theme.palette.mode === 'dark'
			? alpha(theme.palette.common.white, 0.01)
			: alpha(theme.palette.common.black, 0.0)

	const zebraOddRowBg =
		theme.palette.mode === 'dark'
			? alpha(theme.palette.common.white, 0.03)
			: alpha(theme.palette.common.black, 0.02)

	const inputTypeForColumn = useCallback(
		(col: EquipmentColumnDef): React.HTMLInputTypeAttribute | undefined => {
			switch (col.dataType) {
				case 'Date':
					return 'date'
				case 'Time':
					return 'time'
				case 'Currency':
					return 'number'
				case 'Number':
					return 'number'
				case 'Text':
				case 'BillingType':
					return 'text'
				default:
					return undefined
			}
		},
		[],
	)

	return (
		<div className='page-layout'>
			<Box
				sx={{
					px: 2,
					pt: 1.5,
					pb: 2,
					borderBottom: `1px solid ${theme.palette.divider}`,
					background:
						theme.palette.mode === 'dark'
							? alpha(theme.palette.background.paper, 0.6)
							: theme.palette.background.paper,
				}}
			>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={2}
					alignItems={{ xs: 'stretch', sm: 'flex-start' }}
					justifyContent='space-between'
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography
							sx={{
								fontSize: '1.5rem',
							}}
						>
							{pageTitle}
						</Typography>
					</Box>
					<Stack
						direction={{ xs: 'column', sm: 'row' }}
						spacing={1}
						alignItems={{ xs: 'stretch', sm: 'center' }}
						sx={{ flexShrink: 0 }}
					>
						<Chip
							size='small'
							label={
								initialLoading
									? 'Loading…'
									: `${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`
							}
							variant='outlined'
							sx={{
								fontWeight: 500,
								borderColor: 'divider',
								alignSelf: { xs: 'flex-start', sm: 'center' },
							}}
						/>
						<Stack direction='row' spacing={1} flexWrap='wrap'>
							<Button
								variant='outlined'
								color='inherit'
								startIcon={<ViewColumnIcon />}
								onClick={handleOpenAddColumn}
								disabled={initialLoading}
								sx={{
									textTransform: 'none',
									fontWeight: 600,
									borderColor: 'divider',
									color: 'text.primary',
									'&:hover': {
										borderColor: 'text.secondary',
										backgroundColor: alpha(theme.palette.action.hover, 0.8),
									},
								}}
							>
								Add column
							</Button>
							<Button
								variant='contained'
								disableElevation
								startIcon={<AddIcon />}
								onClick={addRow}
								disabled={initialLoading}
								sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}
							>
								Add row
							</Button>
						</Stack>
					</Stack>
				</Stack>
			</Box>

			<Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column' }}>
				<Paper
					elevation={0}
					aria-busy={initialLoading}
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						borderRadius: 1,
						border: `1px solid ${theme.palette.divider}`,
						overflow: 'hidden',
						backgroundColor:
							theme.palette.mode === 'dark'
								? alpha(theme.palette.background.paper, 0.4)
								: theme.palette.background.paper,
					}}
				>
					<Box
						sx={{
							px: 2,
							py: 1.25,
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							borderBottom: `1px solid ${theme.palette.divider}`,
							backgroundColor: alpha(headerBg, theme.palette.mode === 'dark' ? 0.5 : 1),
						}}
					>
						<TableChartOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
						<Typography variant='subtitle2' fontWeight={600} color='text.primary'>
							Log entries
						</Typography>
						{isCanadaTemplate && (
							<FormControlLabel
								sx={{ ml: 'auto', mr: 0 }}
								control={
									<Switch
										size='small'
										checked={showOnlyActive}
										onChange={(e) => setShowOnlyActive(e.target.checked)}
									/>
								}
								label={
									<Typography variant='body2' color='text.secondary'>
										Show only active
									</Typography>
								}
							/>
						)}
					</Box>

					{initialLoading ? (
						<Box
							sx={{
								flex: 1,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								minHeight: 'calc(100vh - 15rem)',
								py: 6,
							}}
						>
							<CircularProgress
								color='success'
								aria-label={isCanadaTemplate ? 'Loading contract charges' : 'Loading equipment log'}
							/>
						</Box>
					) : (
						<TableContainer sx={{ maxHeight: 'calc(100vh - 15rem)' }}>
							<Table
								stickyHeader
								size='small'
								sx={{
									borderCollapse: 'separate',
									'& .MuiTableCell-root': {
										borderBottom: `1px solid ${theme.palette.divider}`,
									},
								}}
							>
								<TableHead>
									<TableRow>
										<TableCell
											sx={{
												width: 88,
												minWidth: 88,
												position: 'sticky',
												left: 0,
												zIndex: 3,
												backgroundColor: headerBg,
												borderBottom: `2px solid ${headerBorder}`,
												boxShadow: `inset -1px 0 0 ${theme.palette.divider}`,
											}}
										>
											<Typography
												variant='caption'
												fontWeight={700}
												letterSpacing='0.06em'
												textTransform='uppercase'
												color='text.secondary'
											>
												#
											</Typography>
										</TableCell>
										{visibleColumns.map((col) => (
											<TableCell
												key={col.id}
												sx={{
													minWidth:
														col.id === 'unitPrice' && billingTypeColumnId
															? 260
															: col.isName
															? 160
															: 120,
													backgroundColor: headerBg,
													borderBottom: `2px solid ${headerBorder}`,
													py: 1.25,
												}}
											>
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between',
														gap: 1,
													}}
												>
													<Box sx={{ minWidth: 0 }}>
														<Typography
															variant='caption'
															component='span'
															fontWeight={700}
															letterSpacing='0.06em'
															textTransform='uppercase'
															color='text.secondary'
															sx={{ display: 'block', lineHeight: 1.2 }}
														>
															{col.label}
														</Typography>
														{col.isName && (
															<Typography
																variant='caption'
																color='primary.main'
																sx={{ display: 'block', mt: 0.25, fontWeight: 600 }}
															>
																Required
															</Typography>
														)}
													</Box>
													{!col.isName && (
														<Tooltip
															title={
																hasUsedRows && isLockableColumn(col)
																	? `“${col.label}” can't be removed while contract charges are used on time entries`
																	: `Remove “${col.label}”`
															}
														>
															<span>
																<IconButton
																	size='small'
																	aria-label={`Remove column ${col.label}`}
																	onClick={() => removeColumn(col)}
																	disabled={hasUsedRows && isLockableColumn(col)}
																	sx={{
																		color: 'text.secondary',
																		'&:hover': {
																			color: 'error.main',
																			backgroundColor: alpha(theme.palette.error.main, 0.08),
																		},
																	}}
																>
																	<DeleteOutlineIcon sx={{ fontSize: 18 }} />
																</IconButton>
															</span>
														</Tooltip>
													)}
												</Box>
											</TableCell>
										))}
										{isCanadaTemplate && (
											<TableCell
												sx={{
													minWidth: 150,
													backgroundColor: headerBg,
													borderBottom: `2px solid ${headerBorder}`,
													py: 1.25,
												}}
											>
												<Typography
													variant='caption'
													component='span'
													fontWeight={700}
													letterSpacing='0.06em'
													textTransform='uppercase'
													color='text.secondary'
												>
													Status
												</Typography>
											</TableCell>
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.length === 0 ? (
										<TableRow>
											<TableCell colSpan={tableColumnCount} align='center' sx={{ py: 6 }}>
												<Stack alignItems='center' spacing={1.5}>
													<TableChartOutlinedIcon sx={{ fontSize: 40, color: 'action.disabled' }} />
													<Typography variant='body1' fontWeight={600} color='text.primary'>
														No entries yet
													</Typography>
													<Button
														variant='contained'
														disableElevation
														startIcon={<AddIcon />}
														onClick={addRow}
														sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
													>
														Add first row
													</Button>
												</Stack>
											</TableCell>
										</TableRow>
									) : displayRows.length === 0 ? (
										<TableRow>
											<TableCell colSpan={tableColumnCount} align='center' sx={{ py: 6 }}>
												<Typography variant='body1' fontWeight={600} color='text.secondary'>
													No active entries
												</Typography>
											</TableCell>
										</TableRow>
									) : (
										displayRows.map(({ row, rowIndex }, displayIndex) => {
											const baseRowBg = displayIndex % 2 === 0 ? zebraEvenRowBg : zebraOddRowBg
											const rowStatus = getRowStatus(row)
											const rowLocked = isRowLocked(row)
											const rowSaved = Number(row.__equipmentId) > 0
											return (
												<TableRow
													key={rowIndex}
													hover
													sx={{
														backgroundColor: baseRowBg,
														'&:hover': {
															backgroundColor: rowHover,
														},
														'&:hover .fleet-equip-sticky-cell': {
															backgroundColor: rowHover,
														},
														'&:last-of-type .MuiTableCell-root': {
															borderBottom: 'none',
														},
													}}
												>
													<TableCell
														className='fleet-equip-sticky-cell'
														sx={{
															verticalAlign: 'middle',
															position: 'sticky',
															left: 0,
															zIndex: 2,
															backgroundColor: baseRowBg,
															boxShadow: `inset -1px 0 0 ${theme.palette.divider}`,
														}}
													>
														<Box
															sx={{
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'space-between',
																gap: 0.5,
																pr: 0.5,
															}}
														>
															<Typography
																variant='body2'
																fontWeight={600}
																color='text.secondary'
																sx={{
																	fontVariantNumeric: 'tabular-nums',
																	minWidth: 20,
																	textAlign: 'center',
																}}
															>
																{displayIndex + 1}
															</Typography>
															{rowLocked ? (
																<Tooltip title={LOCKED_ROW_MESSAGE}>
																	<Box
																		role='img'
																		aria-label='Locked: used on time entries'
																		sx={{ display: 'flex', p: '5px', color: 'text.secondary' }}
																	>
																		<LockOutlinedIcon sx={{ fontSize: 18 }} />
																	</Box>
																</Tooltip>
															) : (
																<Tooltip title='Remove row'>
																	<IconButton
																		size='small'
																		aria-label='Remove row'
																		onClick={() => removeRow(rowIndex)}
																		sx={{
																			color: 'text.secondary',
																			'&:hover': {
																				color: 'error.main',
																				backgroundColor: alpha(theme.palette.error.main, 0.08),
																			},
																		}}
																	>
																		<DeleteOutlineIcon sx={{ fontSize: 18 }} />
																	</IconButton>
																</Tooltip>
															)}
														</Box>
													</TableCell>
													{visibleColumns.map((col) => {
														const isRateHourlyUnitPrice =
															col.id === 'unitPrice' &&
															isRatePlusHourlyBilling(
																billingTypeColumnId ? row[billingTypeColumnId] : '',
															)
														const cellLocked = rowLocked && isLockableColumn(col)

														return (
														<TableCell
															key={col.id}
															sx={{
																verticalAlign: 'middle',
																py: 1,
																px: 1.5,
																opacity: rowStatus.isActive ? 1 : 0.55,
															}}
														>
															{col.dataType === 'BillingType' ? (
																<FormControl fullWidth size='small'>
																	<Select
																		displayEmpty
																		readOnly={cellLocked}
																		value={row[col.id] ?? ''}
																		onChange={(e) => updateCell(rowIndex, col.id, e.target.value)}
																		sx={{
																			fontSize: '0.875rem',
																			backgroundColor: cellLocked
																				? 'transparent'
																				: theme.palette.mode === 'dark'
																				? alpha(theme.palette.common.white, 0.04)
																				: alpha(theme.palette.common.black, 0.02),
																			'& .MuiOutlinedInput-notchedOutline': {
																				borderColor: 'transparent',
																			},
																			'&:hover .MuiOutlinedInput-notchedOutline': {
																				borderColor: 'transparent',
																			},
																			'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
																				borderColor: theme.palette.primary.main,
																			},
																		}}
																	>
																		<MenuItem value=''>
																			<em>Select billing type</em>
																		</MenuItem>
																		{BILLING_TYPE_OPTIONS.map((option) => (
																			<MenuItem key={option} value={option}>
																				{formatBillingTypeLabel(option)}
																			</MenuItem>
																		))}
																	</Select>
																</FormControl>
															) : isRateHourlyUnitPrice ? (
																<RateHourlyUnitPriceCell
																	readOnly={cellLocked}
																	unitPrice={row.unitPrice ?? ''}
																	markup={row[RATE_HOURLY_PERCENT_KEY] ?? ''}
																	payCodeId={toNullableNumber(row[RATE_HOURLY_PAY_CODE_KEY])}
																	payCodes={payCodes}
																	payCodesLoading={payCodesLoading}
																	onChange={(change) => updateLaborRevenueRate(rowIndex, change)}
																/>
															) : (
																<InputBase
																	fullWidth
																	readOnly={cellLocked}
																	value={row[col.id] ?? ''}
																	onChange={(e) => updateCell(rowIndex, col.id, e.target.value)}
																	placeholder={
																		col.isName
																			? 'Enter name'
																			: col.dataType === 'Currency'
																			? '0.00'
																			: `Enter ${col.label.toLowerCase()}`
																	}
																	inputProps={{
																		type: inputTypeForColumn(col),
																		inputMode:
																			col.dataType === 'Number' || col.dataType === 'Currency'
																				? 'decimal'
																				: undefined,
																	}}
																	sx={{
																		'& .MuiInputBase-input': {
																			fontSize: '0.875rem',
																			py: 0.875,
																			px: 1.25,
																			borderRadius: 1,
																			textAlign:
																				col.dataType === 'Number' || col.dataType === 'Currency'
																					? 'right'
																					: 'left',
																			fontVariantNumeric:
																				col.dataType === 'Number' || col.dataType === 'Currency'
																					? 'tabular-nums'
																					: undefined,
																			backgroundColor: cellLocked
																				? 'transparent'
																				: theme.palette.mode === 'dark'
																				? alpha(theme.palette.common.white, 0.04)
																				: alpha(theme.palette.common.black, 0.02),
																			border: `1px solid transparent`,
																			transition: theme.transitions.create(
																				['border-color', 'box-shadow', 'background-color'],
																				{ duration: theme.transitions.duration.shorter },
																			),
																			...(cellLocked && { cursor: 'default' }),
																			'&:hover': cellLocked ? {} : {
																				backgroundColor:
																					theme.palette.mode === 'dark'
																						? alpha(theme.palette.common.white, 0.06)
																						: alpha(theme.palette.common.black, 0.03),
																			},
																			'&:focus': cellLocked ? { outline: 'none' } : {
																				backgroundColor: theme.palette.background.paper,
																				borderColor: theme.palette.primary.main,
																				boxShadow: `0 0 0 3px ${alpha(
																					theme.palette.primary.main,
																					0.2,
																				)}`,
																				outline: 'none',
																			},
																		},
																	}}
																/>
															)}
														</TableCell>
														)
													})}
													{isCanadaTemplate && (
														<TableCell sx={{ verticalAlign: 'middle', py: 1, px: 1.5, whiteSpace: 'nowrap' }}>
															<FormControlLabel
																sx={{ mr: 0 }}
																control={
																	<Switch
																		size='small'
																		checked={rowStatus.isActive}
																		disabled={!rowSaved || statusPendingIds.has(row.__equipmentId)}
																		onChange={() => toggleRowActive(row)}
																		inputProps={{
																			'aria-label': rowStatus.isActive
																				? `Deactivate ${row.name || 'row'}`
																				: `Activate ${row.name || 'row'}`,
																		}}
																	/>
																}
																label={
																	<Typography variant='body2'>
																		{rowStatus.isActive ? 'Active' : 'Inactive'}
																	</Typography>
																}
															/>
														</TableCell>
													)}
												</TableRow>
											)
										})
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>
			</Box>

			<Dialog
				open={addColumnOpen}
				onClose={() => setAddColumnOpen(false)}
				maxWidth='xs'
				fullWidth
				PaperProps={{
					elevation: 8,
					sx: { borderRadius: 2 },
				}}
			>
				<DialogTitle sx={{ pb: 1 }}>
					<Typography variant='h6' component='span' fontWeight={600}>
						Add column
					</Typography>
					<Typography variant='body2' color='text.secondary' sx={{ mt: 0.5, fontWeight: 400 }}>
						New columns appear for every row. You can remove custom columns from the table header.
					</Typography>
				</DialogTitle>
				<Divider />
				<DialogContent sx={{ pt: 3 }}>
					<Stack spacing={2}>
						<TextField
							autoFocus
							fullWidth
							label='Column name'
							placeholder='e.g. Serial number, Location'
							value={newColumnLabel}
							onChange={(e) => handleNewColumnLabelChange(e.target.value)}
							error={billingTypeColumnUnavailable}
							disabled={newColumnType === 'BillingType'}
							helperText={
								newColumnType === 'BillingType'
									? 'Billing type columns must use the name “Billing Type”.'
									: billingTypeColumnUnavailable
										? 'Billing Type is only available for Canada projects.'
										: undefined
							}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault()
									handleConfirmAddColumn()
								}
							}}
							variant='outlined'
							size='small'
						/>
						<FormControl fullWidth size='small'>
							<InputLabel id='fleet-equipment-column-type-label'>Data type</InputLabel>
							<Select
								labelId='fleet-equipment-column-type-label'
								label='Data type'
								value={newColumnType}
								onChange={(e) =>
									handleNewColumnTypeChange(e.target.value as EquipmentColumnDataType)
								}
							>
								<MenuItem value='Text'>Text</MenuItem>
								<MenuItem value='Number'>Number</MenuItem>
								<MenuItem value='Currency'>Currency</MenuItem>
								<MenuItem value='Date'>Date</MenuItem>
								<MenuItem value='Time'>Time</MenuItem>
								{isCanadaTemplate && (
									<MenuItem value='BillingType' disabled={hasBillingTypeColumn}>
										Billing type
									</MenuItem>
								)}
							</Select>
						</FormControl>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5 }}>
					<Button
						onClick={() => setAddColumnOpen(false)}
						variant='contained'
						color='warning'
						sx={{ textTransform: 'none' }}
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirmAddColumn}
						variant='contained'
						disableElevation
						disabled={
							!newColumnLabel.trim() ||
							billingTypeColumnUnavailable ||
							(newColumnType === 'BillingType' && hasBillingTypeColumn)
						}
						sx={{ textTransform: 'none', fontWeight: 600, minWidth: 88 }}
					>
						Add column
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	)
}
